import KError, { KErrorCode } from "@/KError";
import locate from "@/plugins/Location";
import type { ActionContext, Module } from "vuex";
import type { AuthData } from "../plugins/Auth";
import { Auth } from "../plugins/Auth";
import type { SignupContext } from "../plugins/Auth";
import { getNotificationPermission, subscribe, unsubscribe } from "../plugins/Notifications";
import type {
  CollectionResponseInclude,
  Currency,
  ExternalResourceObject,
  Group,
  Member,
  NotificationsSubscription,
  ResourceObject,
} from "./model";

import { config } from "@/utils/config";
import { apiRequest } from "./request";
import { resolveRelationshipUrl } from "./relationships";

// Exported just for testing purposes.
export const auth = new Auth()

export interface LoginPayload {
  email: string
  password: string
  signup?: SignupContext
}

export type AuthorizePayload = {
  force: boolean
} | undefined | null

export interface UserState {
  tokens?: AuthData;
  myUserId?: string;
  myMemberId?: string;
  myMemberUserId?: string;
  /**
   * Current location, provided by device.
   */
  location?: [number, number];
  /**
   * Subscription to push notifications.
   */
  subscription?: NotificationsSubscription;
}

/**
 * Helper function that loads the user data after being logged in and having
 * the credentials.
 */
async function loadUser(context: ActionContext<UserState, never>) {
  const { commit, dispatch, state, getters, rootGetters } = context
  const tokens = state.tokens
  await dispatch("users/load", {});
  // Check after each async operation for a racing logout or newer authorization.
  if (state.tokens !== tokens) return
  const user = rootGetters["users/current"];

  const query = new URLSearchParams({
    include: "group,group.currency,account",
    "page[size]": "1"
  })
  const response = await apiRequest<Member>(
    context,
    `${config.SOCIAL_URL}/users/${user.id}/members?${query}`
  ) as CollectionResponseInclude<Member, ResourceObject>
  if (state.tokens !== tokens) return
  (response.included ?? [])
    .filter(resource => !(resource as ExternalResourceObject).meta?.external)
    .forEach(resource => commit(`${resource.type}/addResource`, resource, { root: true }))
  commit("members/addResources", response.data, { root: true })

  const member = response.data[0]
    ? rootGetters["members/one"](response.data[0].id) as Member & { group: Group }
    : undefined
  let memberUserId: string | undefined

  // A user requesting their first group does not have a membership yet.
  if (member) {
    await dispatch("member-users/loadList", {
      group: member.group.attributes.code,
      filter: {
        user: user.id,
        member: member.id,
      },
      sort: "id",
      pageSize: 1,
    })
    if (state.tokens !== tokens) return
    memberUserId = rootGetters["member-users/currentList"][0]?.id

    // Extract the currency code for store requests to the configured Accounting API.
    const currencyUrl = resolveRelationshipUrl(member.group.relationships.currency);
    const currencyCode = currencyUrl.split('/').slice(-2)[0];

    // pending or deleted members don't have related account. Superadmins neither do.
    if (["active", "disabled", "suspended"].includes(member.attributes.status) && !getters.isSuperadmin) {
      const accountId = member.relationships.account.data.id
      await dispatch("accounts/load", {
        id: accountId, 
        group: currencyCode, 
        include: "settings,currency,currency.settings"
      });
    } else {
    // otherwise get currency at least.
      await dispatch("currencies/load", {
        group: currencyCode,
        include: "settings"
      });
    }
  }

  // A logout or a newer authorization must not publish this obsolete session.
  if (state.tokens !== tokens) return

  // Publish the identity only after its required relationships are available.
  commit("myMemberId", member?.id)
  commit("myMemberUserId", memberUserId)
  commit("myUserId", user.id)
  commit("location", member?.attributes.location?.coordinates)

  // Fetch initial unread notifications count.
  dispatch("notifications/updateUnreadCount", null, { root: true })
    .catch(e => console.warn("Failed to fetch unread count", e));

  // Renew push notification subscription if permission is already granted
  if (getNotificationPermission() === 'granted') {
    dispatch("subscribe").catch(e => console.warn("Failed to restore subscription", e));
  }
}

async function provisionSignup(
  context: ActionContext<UserState, never>,
  email: string,
  signup: SignupContext
) {
  await context.dispatch("users/create", {
    resource: {
      type: "users",
      attributes: {
        name: signup.name,
        email,
        language: signup.language,
      }
    }
  }, { root: true })
}

export default {
  state: () => ({
    tokens: undefined,
    // It is important to define the properties even if undefined in order to add the reactivity.
    myUserId: undefined,
    myMemberId: undefined,
    myMemberUserId: undefined,
    location: undefined,
    subscription: undefined,
  } as UserState),
  getters: {
    isLoggedIn: state =>
      state.myUserId !== undefined &&
      auth.isAuthorized(state.tokens),
    isComplete: (state, getters) =>
      ["active", "disabled", "suspended"].includes(getters.myMember?.attributes.status),
    isSubscribed: state =>
      state.subscription !== undefined,
    myUser: (state, getters, rootState, rootGetters) => {
      if (state.myUserId !== undefined) {
        return rootGetters["users/one"](state.myUserId);
      }
      return undefined;
    },
    myMember: (state, _getters, _rootState, rootGetters) => {
      return state.myMemberId
        ? rootGetters["members/one"](state.myMemberId)
        : undefined
    },
    myMemberUser: (state, _getters, _rootState, rootGetters) => {
      return state.myMemberUserId
        ? rootGetters["member-users/one"](state.myMemberUserId)
        : undefined
    },
    myAccount: (state, getters) => {
      return getters.isComplete 
        ? getters.myMember?.account 
        : false
    },
    myCurrency: (state, getters): Currency | null | undefined => {
      return getters.myAccount?.currency ??
        getters.myMember?.group?.currency
    },
    myGroup: (state, getters) => getters.myMember?.group,
    accessToken: (state) => 
      state.tokens?.accessToken
    ,
    /*
    * Returns true if the group is using the legacy (IntegralCES) accounting API.
    */
    isLegacyAccounting: (state, getters) => {
      // This next check implies that the group is using the legacy accounting API, 
      // since the old api don't have the admins relationship. Otherwise the admin
      // interface is disabled.
      return (getters.myCurrency != null)
        ? !getters.myCurrency.relationships.admins
        : undefined
    },
    isAdmin: (state, getters) => {
      return state.myUserId !== undefined 
        && !getters.isLegacyAccounting
        && getters.myCurrency?.relationships.admins.data.some((r: { id: string }) => r.id === state.myUserId)
    },
    isSuperadmin: (state) => {
      return state.tokens?.scopes?.includes(Auth.SUPERADMIN_SCOPE)
    }
    
  },
  mutations: {
    tokens: (state, tokens) => (state.tokens = tokens),
    myUserId: (state, myUserId) => (state.myUserId = myUserId),
    myMemberId: (state, myMemberId) => (state.myMemberId = myMemberId),
    myMemberUserId: (state, myMemberUserId) => (state.myMemberUserId = myMemberUserId),
    location: (state, location) => (state.location = location),
    subscription: (state, subscription) => (state.subscription = subscription),
  },

  actions: {
    /**
     * Authorize user using email and password.
     */
    login: async (
      context: ActionContext<UserState, never>,
      payload: LoginPayload
    ) => {
      const tokens = await auth.login(payload)
      context.commit("tokens", tokens)
      if (payload.signup) {
        await provisionSignup(context, payload.email, payload.signup)
      }
      await loadUser(context)
    },
    /**
     * Reload the current Social user and their selected membership.
     */
    reloadUser: async (context: ActionContext<UserState, never>) => {
      await loadUser(context)
    },
    /**
     * Silently authorize user using stored credentials. Throws exception (rejects)
     * on failed authorization.
     */
    authorize: async (
      context: ActionContext<UserState, never>,
      payload: AuthorizePayload
    ) => {
      if (!context.getters.isLoggedIn || payload?.force) {
        let sessionTokens = context.state.tokens
        try {
          const storedTokens = await auth.getStoredTokens();
          const tokens = await auth.authorize(storedTokens, payload?.force);
          // A logout or newer authorization cancels this pending attempt.
          if (context.state.tokens !== sessionTokens) {
            return
          }
          context.commit("tokens", tokens);
          sessionTokens = context.state.tokens
          await loadUser(context);
        } catch (error) {
          // Clear only this attempt's credentials, preserving a newer session.
          if (context.state.tokens && context.state.tokens === sessionTokens) {
            await context.dispatch("logout", { authorizationError: true });
          }
          throw error;
        }
      }
    },
    /**
     * Logout current user.
     */
    logout: async (context: ActionContext<UserState, never>, payload: { authorizationError?: boolean }) => {
      // Give max 2 seconds for the unsubscription to complete, but don't block logout.
      const unsubscribePromise = context.dispatch("unsubscribe", {
        unsubscribeFromServer: !(payload?.authorizationError)
      }).catch(e => console.error(e));
      const maxWait = new Promise(resolve => setTimeout(resolve, 2000));
      await Promise.race([unsubscribePromise, maxWait]);

      await auth.logout();
      context.commit("tokens", undefined);
      context.commit("myUserId", undefined);
      context.commit("myMemberId", undefined);
      context.commit("myMemberUserId", undefined);
    },
    /**
     * Get the current location from the device.
     */
    locate: async ({ commit }: ActionContext<UserState, never>) => {
      try {
        const location = await locate()
        commit("location", location);
      } catch (error) {
        if (error instanceof GeolocationPositionError) {
          const codes = [] as KErrorCode[]
          codes[error.TIMEOUT] = KErrorCode.PositionTimeout
          codes[error.POSITION_UNAVAILABLE] = KErrorCode.PositionUnavailable
          codes[error.PERMISSION_DENIED] = KErrorCode.PositionPermisionDenied
          const kerror = new KError(codes[error.code], error.message, undefined, error)
          throw kerror
        }
      }
    },
    /**
     * Subscribe to push notifications
    */
    subscribe: async (context: ActionContext<UserState, never>) => {
      const { state, commit, getters } = context
      // renew subscription if not there, incorrect user and every 24h
      const shouldRenewSubscription = getters.isLoggedIn && getters.myMember && (!state.subscription
        || state.subscription.relationships?.user?.data.id !== getters.myUser.id
        || new Date(state.subscription.attributes.updated) < new Date(Date.now() - 24 * 60 * 60 * 1000)
      )

      if (shouldRenewSubscription) {
        // 1. Subscribe in the Browser
        const attributes = await subscribe()

        // 2. Register subscription in the Backend
        const userId = getters.myUser.id
        const groupCode = getters.myMember.group.attributes.code
        if (!userId || !groupCode) {
          throw new KError(KErrorCode.ScriptError, "Missing user id or group code when subscribing to push notifications");
        }
        
        const body = {
          data: {
            type: "subscriptions",
            attributes,
            relationships: {
              user: {
                data: { id: userId, type: "users" }
              }
            }
          }
        }
        const url = `${config.NOTIFICATIONS_URL}/${groupCode}/subscriptions`

        const response = await apiRequest<NotificationsSubscription>(context, url, 'post', body);

        // 3. Store subscription in the Vuex store
        if (response) {
          commit("subscription", response.data);
        }

        return response
      }
    },
    /**
     * Unsubscribe from push notifications.
     * This function does not throw if the unsubscription fails, but only logs the error in the console.
     */
    unsubscribe: async (context: ActionContext<UserState, never>, payload: { unsubscribeFromServer?: boolean }) => {
      const { state, commit, getters } = context;
      
      // Get data before clearing local state.
      const subscription = state.subscription;
      const groupCode = getters.myMember?.group?.attributes.code;

      // Remove subscription from local state.
      commit("subscription", undefined);

      const promises = [];

      // Unsubscribe from browser.
      promises.push(unsubscribe().catch(e => console.error("Failed to unsubscribe from browser notifications", e)));
      
      // Delete from Backend
      const deleteFromServer = (payload?.unsubscribeFromServer ?? true) && subscription && groupCode !== undefined;
      
      if (deleteFromServer) {
        const url = `${config.NOTIFICATIONS_URL}/${groupCode}/subscriptions/${subscription.id}`;
        const serverPromise = async () => {
          try {
            await apiRequest(context, url, 'delete');
          } catch (error) {
            // Swallow not found errors.
            if (!(error instanceof KError && error.code === KErrorCode.NotFound)) {
              throw error;
            }
          }
        }
        promises.push(serverPromise().catch(e => console.error("Failed to unsubscribe from server notifications", e)));
      }
      await Promise.allSettled(promises);
    }
  }
} as Module<UserState, never>;
